/**
 * Avatars.
 *
 * An uploaded picture wins; otherwise a deterministic mirrored glyph derived
 * from the account id, so nobody is ever a blank square.
 */

import { useEffect, useMemo, useState } from 'react';
import type { PublicUser } from '@/lib/types';
import { fileUrl } from '@/lib/api';
import { cachedStill, isAnimated, stillFrame } from '@/lib/gifstill';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';

/**
 * Which person a rendered face belongs to.
 *
 * The delegated click handler only sees a DOM node and the username on it,
 * and the small profile card wants the person. Rather than stub one out and
 * let the card render half-blank until a fetch lands, each avatar records the
 * object it was given. A WeakMap, so a face that leaves the page takes its
 * entry with it.
 */
const faces = new WeakMap<Element, Props['user']>();

/** The person a face belongs to, or null if the node is not one. */
export function userOfFace(el: Element): Props['user'] | null {
  return faces.get(el) ?? null;
}

function hash32(str: string, seed = 0): number {
  let h = 2166136261 ^ seed;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function rng(seedStr: string): () => number {
  let s = hash32(seedStr, 7) || 1;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5; s >>>= 0;
    return s / 4294967296;
  };
}

type Props = {
  user: Pick<PublicUser, 'id' | 'username' | 'displayName' | 'avatarUrl' | 'avatarAnimated' | 'status'>;
  size?: number;
  presence?: boolean;
  /** Let an animated picture run without hovering - the profile page does. */
  animate?: boolean;
  className?: string;
  /**
   * Corner radius, when the default squircle is wrong for the place.
   * The profile header wants a square picture with a border, which is a
   * different object from the round-ish one a chat line wants.
   */
  radius?: string;
};

/**
 * Whether the picture is a circle, so the status dot knows where to sit.
 *
 * A dot placed for a square hangs off the corner of a circle, in the gap
 * where the picture is not. With no explicit radius the shape comes from
 * --avatar-radius, which is a circle unless somebody chose otherwise.
 */
function isRound(radius?: string): boolean {
  if (radius) return radius === '50%';
  if (typeof window === 'undefined') return true;
  const set = getComputedStyle(document.documentElement)
    .getPropertyValue('--avatar-radius').trim();
  return !set || set === '50%';
}

export function Avatar({ user, size = 36, presence, animate, className, radius }: Props) {
  const glyph = useMemo(() => identicon(user.id || user.username, size), [user.id, user.username, size]);
  const picture = user.avatarUrl ? fileUrl(user.avatarUrl) : null;

  // A moving picture is frozen to its first frame until you point at it, so a
  // chat log full of GIF avatars is not a slideshow.
  const moving = !!picture && (user.avatarAnimated || isAnimated(picture));
  const [hover, setHover] = useState(false);
  const [still, setStill] = useState(() => (picture ? cachedStill(picture) : null));

  useEffect(() => {
    if (!moving || !picture) return;
    let live = true;
    void stillFrame(picture).then((data) => { if (live) setStill(data); });
    return () => { live = false; };
  }, [moving, picture]);

  /* Animated pictures play on their own when that is switched on in Chat. */
  const autoplay = useSession((s) => !!s.settings?.gifAutoplay);
  const src = moving && !animate && !autoplay && !hover && still ? still : picture;

  const body = (
    <span
      /*
       * Every avatar says whose it is.
       *
       * One delegated handler on the shell turns that into a click that
       * opens the profile - see lib/profileClicks. Doing it here rather than
       * at each of the hundred call sites is why an avatar in the leaderboard
       * behaves like an avatar in a message, and it stays out of the way of
       * anything already interactive: a picture inside a button belongs to
       * the button.
       */
      data-profile={user.username || undefined}
      ref={(el) => { if (el) faces.set(el, user); }}
      // The shape comes from Appearance, so changing it there changes every
      // avatar at once rather than each caller deciding.
      className={cn('smooth-clip relative block flex-none overflow-hidden bg-surface-3', !radius && 'rounded-[var(--avatar-radius,50%)]', className)}
      style={{ width: size, height: size, ...(radius ? { borderRadius: radius } : null) }}
      onPointerEnter={moving ? () => setHover(true) : undefined}
      onPointerLeave={moving ? () => setHover(false) : undefined}
    >
      {picture ? (
        <img src={src ?? undefined} alt="" width={size} height={size} className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <span className="block h-full w-full" dangerouslySetInnerHTML={{ __html: glyph }} />
      )}
    </span>
  );

  if (!presence) return body;

  return (
    <span className="relative block flex-none" style={{ width: size, height: size }}>
      {body}
      <PresenceDot user={user} size={size} round={isRound(radius)} />
    </span>
  );
}

/**
 * The online dot.
 *
 * Its own component because a framed picture has to draw it after the frame -
 * the ring is painted over the picture, and it was painting over the dot too.
 *
 * Where it sits depends on the shape. On a square the corner of the box is
 * the corner of the picture. On a circle the box corner is outside the
 * picture entirely, and the point at 45 degrees is about 15% of the width in
 * from each edge - which is where the dot lands with no offset at all.
 */
export function PresenceDot({ user, size, round }: {
  user: Pick<PublicUser, 'status'>;
  size: number;
  round?: boolean;
}) {
  const dot = Math.max(8, size * 0.28);
  return (
    <i
      className={cn(
        'absolute rounded-full',
        round ? 'right-0 bottom-0' : '-right-0.5 -bottom-0.5',
        user.status === 'online' ? 'bg-good'
          : user.status === 'idle' ? 'bg-warn'
            : user.status === 'dnd' ? 'bg-bad'
              : 'bg-ghost'
      )}
      style={{
        width: dot,
        height: dot,
        // Scaled with the picture: a 2px collar vanishes at 132px.
        border: Math.max(2, Math.round(size * 0.028)) + 'px solid var(--bg-1)'
      }}
    />
  );
}

function identicon(seed: string, size: number): string {
  const rand = rng(seed || 'anon');
  const hue = Math.floor(rand() * 360);
  const hue2 = (hue + 40 + Math.floor(rand() * 120)) % 360;
  const dark = `hsl(${hue} 32% 22%)`;
  const darker = `hsl(${hue2} 34% 13%)`;
  const ink = `hsl(${hue} 62% 68%)`;
  const ink2 = `hsl(${hue2} 58% 60%)`;

  let cells = '';
  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < 3; x++) {
      if (rand() > 0.52) continue;
      const c = rand() > 0.72 ? ink2 : ink;
      cells += `<rect x="${x * 20}" y="${y * 20}" width="20" height="20" fill="${c}"/>`;
      if (x < 2) cells += `<rect x="${(4 - x) * 20}" y="${y * 20}" width="20" height="20" fill="${c}"/>`;
    }
  }

  const gid = 'g' + hash32(seed, 3).toString(36);
  return `<svg viewBox="0 0 100 100" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">` +
    `<defs><linearGradient id="${gid}" x1="0" y1="0" x2="1" y2="1">` +
    `<stop offset="0" stop-color="${dark}"/><stop offset="1" stop-color="${darker}"/>` +
    `</linearGradient></defs><rect width="100" height="100" fill="url(#${gid})"/>${cells}</svg>`;
}
